import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

// User competency interface
export interface UserCompetency {
  name: string;
  _id?: string;
  weight?: number; // Weight percentage for the competency
  // Assessment fields
  ratingScore?: number;
  evidence?: string;
  attachments?: Array<{ name: string; url: string }>;
  comments?: string;
}

// User competencies data interface
export interface UserCompetenciesData {
  enableCompetencyAssessment: boolean;
  contributionScore: number;
  competencies: UserCompetency[];
}

// Employee competency interface
export interface EmployeeCompetency {
  _id: string;
  name: string;
  jobTitle: string;
  email: string;
  contributionScore: number;
  enableCompetencyAssessment: boolean;
}

// State interface
interface EmployeeCompetencyState {
  employees: EmployeeCompetency[];
  userCompetencies: UserCompetenciesData | null;
  loading: boolean;
  error: string | null;
}

const initialState: EmployeeCompetencyState = {
  employees: [],
  userCompetencies: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchEmployeeCompetencyAssessments = createAsyncThunk(
  'employeeCompetency/fetchAssessments',
  async ({ annualTargetId, quarter }: { annualTargetId: string; quarter: string }) => {
    try {
      const response = await api.get('/employee-competency/assessments', {
        params: { annualTargetId, quarter }
      });
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch employee competency assessments';
    }
  }
);

export const updateContributionScore = createAsyncThunk(
  'employeeCompetency/updateContributionScore',
  async ({ 
    userId, 
    annualTargetId, 
    quarter, 
    contributionScore 
  }: { 
    userId: string; 
    annualTargetId: string; 
    quarter: string; 
    contributionScore: number;
  }) => {
    try {
      const response = await api.put('/employee-competency/contribution-score', {
        userId,
        annualTargetId,
        quarter,
        contributionScore
      });
      return { userId, contributionScore };
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to update contribution score';
    }
  }
);

export const bulkUpdateContributionScores = createAsyncThunk(
  'employeeCompetency/bulkUpdateContributionScores',
  async ({ 
    userIds, 
    annualTargetId, 
    quarter, 
    contributionScore 
  }: { 
    userIds: string[]; 
    annualTargetId: string; 
    quarter: string; 
    contributionScore: number;
  }) => {
    try {
      const response = await api.put('/employee-competency/bulk-contribution-score', {
        userIds,
        annualTargetId,
        quarter,
        contributionScore
      });
      return { userIds, contributionScore };
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to bulk update contribution scores';
    }
  }
);

export const toggleEnableCompetencyAssessment = createAsyncThunk(
  'employeeCompetency/toggleEnableCompetencyAssessment',
  async ({ 
    userId, 
    annualTargetId, 
    quarter, 
    enableCompetencyAssessment 
  }: { 
    userId: string; 
    annualTargetId: string; 
    quarter: string; 
    enableCompetencyAssessment: boolean;
  }) => {
    try {
      const response = await api.put('/employee-competency/toggle-enable', {
        userId,
        annualTargetId,
        quarter,
        enableCompetencyAssessment
      });
      return { userId, enableCompetencyAssessment };
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to toggle competency assessment';
    }
  }
);

export const fetchUserCompetencies = createAsyncThunk(
  'employeeCompetency/fetchUserCompetencies',
  async ({ annualTargetId, quarter, userId }: { annualTargetId: string; quarter: string; userId?: string }) => {
    try {
      const params: any = { annualTargetId, quarter };
      if (userId) {
        params.userId = userId;
      }
      
      const response = await api.get('/employee-competency/user-competencies', {
        params
      });
      
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to fetch user competencies';
    }
  }
);

export const updateUserCompetencies = createAsyncThunk(
  'employeeCompetency/updateUserCompetencies',
  async ({ 
    annualTargetId, 
    quarter, 
    competencies 
  }: { 
    annualTargetId: string; 
    quarter: string; 
    competencies: UserCompetency[];
  }) => {
    try {
      const response = await api.put('/employee-competency/user-competencies', {
        annualTargetId,
        quarter,
        competencies
      });
      return competencies;
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to update user competencies';
    }
  }
);

export const bulkEnableCompetencyAssessment = createAsyncThunk(
  'employeeCompetency/bulkEnableCompetencyAssessment',
  async ({ 
    userIds, 
    annualTargetId, 
    quarter, 
    enableCompetencyAssessment 
  }: { 
    userIds: string[]; 
    annualTargetId: string; 
    quarter: string; 
    enableCompetencyAssessment: boolean;
  }) => {
    try {
      const response = await api.put('/employee-competency/bulk-toggle-enable', {
        userIds,
        annualTargetId,
        quarter,
        enableCompetencyAssessment
      });
      return { userIds, enableCompetencyAssessment };
    } catch (error: any) {
      throw error.response?.data?.message || 'Failed to bulk enable competency assessment';
    }
  }
);

// Slice
const employeeCompetencySlice = createSlice({
  name: 'employeeCompetency',
  initialState,
  reducers: {
    clearEmployees: (state) => {
      state.employees = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch assessments
    builder.addCase(fetchEmployeeCompetencyAssessments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEmployeeCompetencyAssessments.fulfilled, (state, action) => {
      state.loading = false;
      state.employees = action.payload;
      state.error = null;
    });
    builder.addCase(fetchEmployeeCompetencyAssessments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch assessments';
    });

    // Update individual contribution score
    builder.addCase(updateContributionScore.pending, (state) => {
      state.error = null;
    });
    builder.addCase(updateContributionScore.fulfilled, (state, action) => {
      const { userId, contributionScore } = action.payload;
      const employee = state.employees.find(emp => emp._id === userId);
      if (employee) {
        employee.contributionScore = contributionScore;
      }
      state.error = null;
    });
    builder.addCase(updateContributionScore.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update contribution score';
    });

    // Bulk update contribution scores
    builder.addCase(bulkUpdateContributionScores.pending, (state) => {
      state.error = null;
    });
    builder.addCase(bulkUpdateContributionScores.fulfilled, (state, action) => {
      const { userIds, contributionScore } = action.payload;
      state.employees.forEach(employee => {
        if (userIds.includes(employee._id)) {
          employee.contributionScore = contributionScore;
        }
      });
      state.error = null;
    });
    builder.addCase(bulkUpdateContributionScores.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to bulk update contribution scores';
    });

    // Toggle enable competency assessment
    builder.addCase(toggleEnableCompetencyAssessment.pending, (state) => {
      state.error = null;
    });
    builder.addCase(toggleEnableCompetencyAssessment.fulfilled, (state, action) => {
      const { userId, enableCompetencyAssessment } = action.payload;
      const employee = state.employees.find(emp => emp._id === userId);
      if (employee) {
        employee.enableCompetencyAssessment = enableCompetencyAssessment;
      }
      state.error = null;
    });
    builder.addCase(toggleEnableCompetencyAssessment.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to toggle competency assessment';
    });

    // Fetch user competencies
    builder.addCase(fetchUserCompetencies.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserCompetencies.fulfilled, (state, action) => {
      state.loading = false;
      state.userCompetencies = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUserCompetencies.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch user competencies';
    });

    // Update user competencies
    builder.addCase(updateUserCompetencies.pending, (state) => {
      state.error = null;
    });
    builder.addCase(updateUserCompetencies.fulfilled, (state, action) => {
      if (state.userCompetencies) {
        state.userCompetencies.competencies = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateUserCompetencies.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update user competencies';
    });

    // Bulk enable competency assessment
    builder.addCase(bulkEnableCompetencyAssessment.pending, (state) => {
      state.error = null;
    });
    builder.addCase(bulkEnableCompetencyAssessment.fulfilled, (state, action) => {
      const { userIds, enableCompetencyAssessment } = action.payload;
      state.employees.forEach(employee => {
        if (userIds.includes(employee._id)) {
          employee.enableCompetencyAssessment = enableCompetencyAssessment;
        }
      });
      state.error = null;
    });
    builder.addCase(bulkEnableCompetencyAssessment.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to bulk enable competency assessment';
    });
  },
});

export const { clearEmployees, clearError } = employeeCompetencySlice.actions;

// Selectors
export const selectEmployeeCompetencyEmployees = (state: { employeeCompetency: EmployeeCompetencyState }) => 
  state.employeeCompetency.employees;
export const selectEmployeeCompetencyLoading = (state: { employeeCompetency: EmployeeCompetencyState }) => 
  state.employeeCompetency.loading;
export const selectEmployeeCompetencyError = (state: { employeeCompetency: EmployeeCompetencyState }) => 
  state.employeeCompetency.error;
export const selectUserCompetencies = (state: { employeeCompetency: EmployeeCompetencyState }) => 
  state.employeeCompetency.userCompetencies;

export default employeeCompetencySlice.reducer;
